import React from 'react';
import AppContext from './common/context/app-context';
import useSetAppState from './common/hooks/useSetAppState';
import {RouterProvider} from 'react-router-dom';
import ReactGA4 from 'react-ga4';
import useCreateBrowserRouter from './common/hooks/useCreateBrowserRouter';
import Modal from 'react-modal';
import {QueryClient, QueryClientProvider} from 'react-query';
import {initializeApp} from 'firebase/app';
import {firebaseConfig} from './firebase';
import {Toaster} from 'react-hot-toast';
import moment from 'moment';
import mixpanel from 'mixpanel-browser';

// Detect if running in React Native wrapper
declare global {
  interface Window {
    isReactNativeWrapper?: boolean;
    wrapperPlatform?: string;
    Cookiebot?: {
      consent: {
        statistics: boolean;
        marketing: boolean;
        preferences: boolean;
      };
    };
  }
}

// Use getters to read values dynamically since injected JS may run after initial module load
export const getIsReactNativeWrapper = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const param = urlParams.get('isReactNativeWrapper');
    if (param !== null) {
      return param === 'true';
    }
    return window.isReactNativeWrapper === true;
  }
  return false;
};
export const getWrapperPlatform = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const param = urlParams.get('wrapperPlatform');
    if (param !== null) {
      return param;
    }
    return window.wrapperPlatform;
  }
  return 'web';
};

/** Optional flag (query `isV2AppVersion=true`) for newer app features, e.g. in-game close → React Native. */
export const getIsV2AppVersion = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const param = urlParams.get('isV2AppVersion') ?? urlParams.get('isAppV2Version');
    if (param !== null) {
      return param === 'true';
    }
  }
  return false;
};

export const getSessionId = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('sessionId') || undefined;
  }
  return undefined;
};

// @deprecated: Keep these for backward compatibility, but they may not work correctly for early code execution. Use the getter functions above instead.
export const isReactNativeWrapper = typeof window !== 'undefined' && window.isReactNativeWrapper === true;
export const wrapperPlatform = typeof window !== 'undefined' ? window.wrapperPlatform : undefined;

initializeApp(firebaseConfig);
ReactGA4.initialize(`${process.env.REACT_APP_GOOGLE_GA}`);

try {
  mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN as string, {
    debug: true,
    persistence: 'localStorage',
    opt_out_tracking_by_default: false,
    ignore_dnt: true
  });
  mixpanel.opt_in_tracking();
} catch {
}

if(process.env.REACT_APP_ENVIRONMENT != "production"){
  document.title = `${process.env.REACT_APP_ENVIRONMENT} Flipped on Dogs - Daily Learning Game | Learn to Speak Dog with Quick, Fun Lessons from the Pros`;
}

moment().utcOffset(new Date().getTimezoneOffset());

const rootElement = document.querySelector("#root");

Modal.setAppElement(rootElement as HTMLElement);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      cacheTime: 0,
    },
  },
});

function App() {
  const appState = useSetAppState();
  const router = useCreateBrowserRouter();
  return  (
    <QueryClientProvider client={queryClient}>
      <AppContext.Provider value={appState}>
        <RouterProvider router={router} />
        <Toaster/>
      </AppContext.Provider>
    </QueryClientProvider>
  );
}

export default App;
